import { Box, CssBaseline } from "@mui/joy";
import type { SxProps } from "@mui/joy/styles/types";
import { Children, type ReactNode } from "react";
import { useDesignV2Decided } from "../flags/designV2Store";
import { useAppearancePolicy } from "../platform/PlatformProvider";
import { isInIframe } from "../utils/browserUtils";
import { HeaderBrand, HeaderFrame } from "./HeaderFrame";
import PrimepointMark from "./PrimepointMark";
import { AppearanceProvider } from "../theme/AppearanceProvider";
import useTabletShell from "../hooks/useTabletShell";
import { resolveAppChrome } from "../navigation/appChrome";

const styles = {
    // Before the design is known: the mark in the middle, as index.html draws it before the app.
    undecided: {
        position: "relative",
        minHeight: "100dvh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "background.body",
    },
    undecidedActions: { position: "absolute", top: 0, right: 0, p: 1 },
} satisfies Record<string, SxProps>;

/** Public chrome only: no tenant hooks, requests or cached project names.
 * The real header fills its slots once this document's account is admitted. On the web, until
 * the project's design is known (its flag answers after admission, `DesignV2Gate`), it draws
 * neither design's frame. */
export default function BootstrapShell({
    children,
    actions,
}: {
    children?: ReactNode;
    actions?: ReactNode;
}) {
    const { native, redesigned } = useAppearancePolicy();
    const decided = useDesignV2Decided() || native;
    const tablet = useTabletShell();
    const inIframe = isInIframe();
    const chrome = resolveAppChrome({
        pathname: window.location.pathname,
        redesigned,
        tablet,
        inIframe,
    });
    const navigation = redesigned ? "rail" : "horizontal";
    const content = !decided ? (
        <Box sx={styles.undecided}>
            {actions && <Box sx={styles.undecidedActions}>{actions}</Box>}
            {Children.toArray(children).length > 0 ? (
                children
            ) : (
                <PrimepointMark size={56} loading label="Opening" />
            )}
        </Box>
    ) : native || inIframe || (chrome !== "header" && chrome !== "workspace") ? (
        <>
            {actions}
            {children}
        </>
    ) : (
        <Box sx={{ minHeight: "100dvh", bgcolor: "background.body" }}>
            <HeaderFrame navigation={navigation}>
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        minHeight: 32,
                        width: "100%",
                        justifyContent: "space-between",
                        gap: 2,
                    }}
                >
                    {/* Nothing else to show yet: the mark says the project is opening. */}
                    <HeaderBrand navigation={navigation} loading={!children} />
                    {actions}
                </Box>
            </HeaderFrame>
            {children}
        </Box>
    );
    return (
        <AppearanceProvider>
            <CssBaseline />
            {content}
        </AppearanceProvider>
    );
}
